import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { AuthInput } from '@/components/common/AuthInput';

// Validation Schema
const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export interface LoginFormProps {
  onSubmit: (data: LoginFormValues) => Promise<void>;
  onForgot: () => void;
  error?: string | null; // Backend errors
  isLoading?: boolean;
  formId: string; // External submit button linkage
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit,
  onForgot,
  error: backendError,
  isLoading = false,
  formId,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} className='w-full space-y-4'>
      <AuthInput
        id='email'
        type='email'
        label='Email'
        placeholder='Enter your email'
        autoComplete='email'
        disabled={isLoading}
        error={errors.email?.message}
        {...register('email')}
      />

      <div className='!mt-[30px] space-y-2'>
        <AuthInput
          id='password'
          type='password'
          label='Password'
          placeholder='Enter your password'
          autoComplete='current-password'
          disabled={isLoading}
          error={errors.password?.message}
          {...register('password')}
        />

        {/* Forgot Password Link */}
        <div className='ml-1 flex justify-start'>
          <button
            type='button'
            onClick={onForgot}
            className='font-sans text-14 font-medium leading-21 text-auth-link transition-colors hover:text-white fold:text-gray-530 fold:hover:text-gray-600'
            disabled={isLoading}
          >
            Forgot Password
          </button>
        </div>
      </div>

      {backendError && (
        <div className='rounded-md border border-red-500/20 bg-red-500/10 p-3'>
          <p className='text-center text-sm text-red-400'>{backendError}</p>
        </div>
      )}
    </form>
  );
};

LoginForm.displayName = 'LoginForm';
